import { useState, useEffect, useCallback } from 'react';
import useCallApiAndLoad from '../../../hooks/useCallApiAndLoad';
import useAuth from '../../../hooks/useAuth';
import { getMasterPropertiesEndpoint } from '../services/propertyService';
import { fetchResponseAdapter } from '../../../adapters/fetchAdapter';
import { PropertiesList } from '../models/propertyModel';
import {
  getRecommendationsEndpoint,
  createRecommendationEndpoint,
  deleteRecommendationEndpoint,
  updatePositionsEndpoint,
} from '../services/propertyRecommendationService';

export interface PropertyRecommendation {
  id: string;
  property_id: string;
  property: PropertiesList;
  marketplace_type: 'for_rent' | 'for_sale';
  position: number;
  is_active: boolean;
  created_at: string;
}

export interface PendingSyncAction {
  id: string;
  actionType: 'add' | 'delete';
  propertyCode: string | number;
  marketplaceType: 'for_rent' | 'for_sale';
}

const usePropertyRecommendations = () => {
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  const { isLoading: searchLoading, callEndpoint } = useCallApiAndLoad();

  const [recommendations, setRecommendations] = useState<PropertyRecommendation[]>([]);
  const [serverRecommendations, setServerRecommendations] = useState<PropertyRecommendation[]>([]);
  const [searchError, setSearchError] = useState<string>('');
  const [pendingActions, setPendingActions] = useState<PendingSyncAction[]>(() => {
    try {
      const stored = sessionStorage.getItem('recommendations_pending_actions');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const addPendingAction = (action: Omit<PendingSyncAction, 'id'>) => {
    const newAction: PendingSyncAction = {
      ...action,
      id: Math.random().toString(36).substring(2, 9),
    };
    const updated = [...pendingActions, newAction];
    setPendingActions(updated);
    sessionStorage.setItem('recommendations_pending_actions', JSON.stringify(updated));
  };

  const clearPendingActions = (type: 'for_rent' | 'for_sale') => {
    const updated = pendingActions.filter((a) => a.marketplaceType !== type);
    setPendingActions(updated);
    sessionStorage.setItem('recommendations_pending_actions', JSON.stringify(updated));
  };

  // Load server data
  const loadRecommendations = useCallback(async () => {
    const { status, data } = await callEndpoint(getRecommendationsEndpoint(token));
    if (status === 200) {
      const response = fetchResponseAdapter(data);
      const list: PropertyRecommendation[] = response.data || [];
      const sorted = [...list].sort((a, b) => {
        if (a.marketplace_type !== b.marketplace_type) {
          return a.marketplace_type === 'for_rent' ? -1 : 1;
        }
        return a.position - b.position;
      });
      setRecommendations(sorted);
      setServerRecommendations(sorted);
    }
  }, [callEndpoint, token]);

  useEffect(() => {
    loadRecommendations().catch((e) => console.error('Error al cargar recomendaciones:', e));
  }, [loadRecommendations]);

  // Determine if there are local changes in the positions or pending sync by type
  const hasChanges = (type: 'for_rent' | 'for_sale'): boolean => {
    const typeRecommendations = recommendations.filter((r) => r.marketplace_type === type);
    const hasPositionChanges = typeRecommendations.some((item) => {
      const sItem = serverRecommendations.find((s) => s.id === item.id);
      return !sItem || sItem.position !== item.position;
    });
    const isPendingSync = pendingActions.some((a) => a.marketplaceType === type);
    return hasPositionChanges || isPendingSync;
  };

  // Search for published property in the backend by Code
  const searchProperty = async (code: string): Promise<PropertiesList | null> => {
    setSearchError('');
    if (!code.trim()) {
      setSearchError('El código de propiedad no puede estar vacío.');
      return null;
    }

    const endpoint = getMasterPropertiesEndpoint(`code=${code.trim()}`, token);
    const { status, data } = await callEndpoint(endpoint);

    if (status === 200) {
      const response = fetchResponseAdapter(data);
      const items = response.data.items || [];
      if (items.length === 0) {
        setSearchError(`No se encontró ninguna propiedad con el código: ${code}`);
        return null;
      }

      const property: PropertiesList = items[0];

      // Validate if it is published for rent or sale
      const isRentPublished = property.state === 'published';
      const isSalePublished = property.sale_state === 'published';

      if (!isRentPublished && !isSalePublished) {
        setSearchError(`La propiedad con código "${code}" no está publicada para alquiler ni para venta.`);
        return null;
      }

      return property;
    } else {
      setSearchError('Error al consultar el servidor de propiedades.');
      return null;
    }
  };

  // Add Recommendation
  const addRecommendation = async (
    property: PropertiesList,
    type: 'for_rent' | 'for_sale',
    position: number
  ): Promise<{ success: boolean; message?: string }> => {
    // Validate if the property is already recommended for this type in the UI
    const alreadyRecommended = recommendations.some(
      (item) => item.property_id === property.id && item.marketplace_type === type
    );

    if (alreadyRecommended) {
      return {
        success: false,
        message: `La propiedad con código ${property.code} ya está en la lista de recomendados para ${type === 'for_rent' ? 'Renta' : 'Venta'
          }.`,
      };
    }

    if (hasChanges(type)) {
      const typeRecommendations = recommendations.filter((r) => r.marketplace_type === type);
      const payloadPositions = typeRecommendations.map((r) => ({ id: r.id, position: r.position }));
      const putResult = await callEndpoint(updatePositionsEndpoint({ positions: payloadPositions }, token));
      if (putResult.status !== 200) {
        return {
          success: false,
          message: 'No se pudieron guardar las posiciones previas antes de agregar la nueva recomendación.',
        };
      }
    }

    // Execute the creation in the backend
    const postResult = await callEndpoint(
      createRecommendationEndpoint(
        {
          property_id: property.id,
          marketplace_type: type,
          position,
        },
        token
      )
    );

    if (postResult.status === 200 || postResult.status === 201) {
      await loadRecommendations();
      addPendingAction({
        actionType: 'add',
        propertyCode: property.code,
        marketplaceType: type,
      });
      return { success: true };
    } else {
      const errorMsg = postResult.data?.message || 'Error al agregar recomendación en el servidor.';
      return { success: false, message: errorMsg };
    }
  };

  // Remove Recommendation
  const removeRecommendation = async (id: string): Promise<{ success: boolean; message?: string }> => {
    const recToRemove = recommendations.find((r) => r.id === id);
    const type = recToRemove?.marketplace_type;

    if (type && hasChanges(type)) {
      const typeRecommendations = recommendations.filter((r) => r.marketplace_type === type);
      const payloadPositions = typeRecommendations.map((r) => ({ id: r.id, position: r.position }));
      const putResult = await callEndpoint(updatePositionsEndpoint({ positions: payloadPositions }, token));
      if (putResult.status !== 200) {
        return {
          success: false,
          message: 'No se pudieron guardar las posiciones previas antes de eliminar la recomendación.',
        };
      }
    }

    const deleteResult = await callEndpoint(deleteRecommendationEndpoint(id, token));
    if (deleteResult.status === 200) {
      await loadRecommendations();
      if (type && recToRemove?.property?.code) {
        addPendingAction({
          actionType: 'delete',
          propertyCode: recToRemove.property.code,
          marketplaceType: type,
        });
      }
      return { success: true };
    } else {
      const errorMsg = deleteResult.data?.message || 'Error al eliminar recomendación del servidor.';
      return { success: false, message: errorMsg };
    }
  };

  // Reorder positions locally in memory
  const reorderRecommendations = (type: 'for_rent' | 'for_sale', startIndex: number, endIndex: number) => {
    // Filter the elements of the specified type and order them by their current position
    const typeItems = recommendations
      .filter((item) => item.marketplace_type === type)
      .sort((a, b) => a.position - b.position);

    // If the indexes are invalid or equal, exit
    if (startIndex < 0 || startIndex >= typeItems.length || endIndex < 0 || endIndex >= typeItems.length) return;
    if (startIndex === endIndex) return;

    // Remove from the source index and place in the destination index
    const [reorderedItem] = typeItems.splice(startIndex, 1);
    typeItems.splice(endIndex, 0, reorderedItem);

    // Assign new contiguous positions
    const updatedTypeItems = typeItems.map((item, index) => ({
      ...item,
      position: index + 1,
    }));

    // Merge with the elements of the other type
    const otherItems = recommendations.filter((item) => item.marketplace_type !== type);
    const newRecommendationsList = [...otherItems, ...updatedTypeItems];

    // Order the resulting list by consistency
    const sorted = newRecommendationsList.sort((a, b) => {
      if (a.marketplace_type !== b.marketplace_type) {
        return a.marketplace_type === 'for_rent' ? -1 : 1;
      }
      return a.position - b.position;
    });

    setRecommendations(sorted);
  };

  // Save positions in batch 
  const savePositions = async (type: 'for_rent' | 'for_sale'): Promise<{ success: boolean; message?: string }> => {
    const typeRecommendations = recommendations.filter((r) => r.marketplace_type === type);
    const payloadPositions = typeRecommendations.map((r) => ({ id: r.id, position: r.position }));
    const result = await callEndpoint(updatePositionsEndpoint({ positions: payloadPositions }, token));
    if (result.status === 200) {
      await loadRecommendations();
      clearPendingActions(type);
      return { success: true };
    } else {
      const errorMsg = result.data?.message || 'Error al guardar las posiciones en el servidor.';
      return { success: false, message: errorMsg };
    }
  };

  // Cancel changes locally by type
  const cancelChanges = (type: 'for_rent' | 'for_sale') => {
    const otherRecommendations = recommendations.filter((r) => r.marketplace_type !== type);
    const serverTypeRecommendations = serverRecommendations.filter((r) => r.marketplace_type === type);
    const newRecommendations = [...otherRecommendations, ...serverTypeRecommendations].sort((a, b) => {
      if (a.marketplace_type !== b.marketplace_type) {
        return a.marketplace_type === 'for_rent' ? -1 : 1;
      }
      return a.position - b.position;
    });
    setRecommendations(newRecommendations);
    clearPendingActions(type);
  };

  return {
    recommendations,
    searchLoading,
    searchError,
    hasChangesRent: hasChanges('for_rent'),
    hasChangesSale: hasChanges('for_sale'),
    pendingActions,
    searchProperty,
    addRecommendation,
    removeRecommendation,
    reorderRecommendations,
    savePositions,
    cancelChanges,
  };
};

export default usePropertyRecommendations;
